import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import Avatar from '../ui/Avatar';
import ConfirmationDialog from '../ui/ConfirmationDialog';
import { Student, LearningPath, Class, StudentGroup } from '../../data/types';
import { dataService } from '../../services/dataService';
import PageContainer from '../layouts/PageContainer';

interface LearningPathAssignmentScreenProps {
  path?: LearningPath;
}

const LearningPathAssignmentScreen: React.FC<LearningPathAssignmentScreenProps> = ({ path }) => {
  const navigate = useNavigate();
  const location = useLocation();
  // Always start with students selection as first step
  const [selectedMode, setSelectedMode] = useState<'students' | 'paths'>('students');
  
  // Extract pathId from URL if present
  const getPathIdFromUrl = () => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('pathId');
  };
  
  const pathId = getPathIdFromUrl();
  
  // Learning paths data from service
  const [availablePaths, setAvailablePaths] = useState<LearningPath[]>([]);
  
  // Fetch learning paths on component mount
  useEffect(() => {
    const fetchLearningPaths = async () => {
      try {
        const paths = await dataService.getLearningPaths();
        setAvailablePaths(paths);
      } catch (error) {
        console.error('Error fetching learning paths:', error);
      }
    };
    
    fetchLearningPaths();
  }, []);
  
  // Always start with students selection as first step regardless of path selection
  useEffect(() => {
    setSelectedMode('students');
    
    // Clear existing paths and add the pre-selected path if it exists
    if (pathId) {
      const pathToAdd = availablePaths.find(p => p.id === pathId);
      if (pathToAdd) {
        setSelectedPaths([pathToAdd]); // Replace with just this path instead of adding to existing array
      }
    } else if (path) {
      // If path prop is provided directly
      setSelectedPaths([path]); // Replace with just this path instead of adding to existing array
    }
    
    // Fetch student groups
    const fetchGroups = async () => {
      try {
        const groups = await dataService.getStudentGroups();
        setStudentGroups(groups);
      } catch (error) {
        console.error('Error fetching student groups:', error);
      }
    };
    
    fetchGroups();
  }, [pathId, path, availablePaths]);
  
  // Format date as YYYY-MM-DD for input type="date"
  const formatDateForInput = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  // State for assignment data
  const [searchQuery, setSearchQuery] = useState('');
  const [startDate, setStartDate] = useState(formatDateForInput(new Date())); // Default to current day
  const [endDate, setEndDate] = useState('');
  const [assignmentTab, setAssignmentTab] = useState<'students' | 'classes' | 'groups'>('students');
  const [studentGroups, setStudentGroups] = useState<StudentGroup[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  
  // New state for multi-path selection and configuration
  const [selectedPaths, setSelectedPaths] = useState<LearningPath[]>([]);
  const [pathSearchQuery, setPathSearchQuery] = useState('');
  const [gradeFilter, setGradeFilter] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('');
  const [standardFilter, setStandardFilter] = useState('');
  
  // State for success confirmation dialog
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  
  // Path assignment constraints - We're keeping these state variables even though they
  // might not be used yet, as they are likely to be implemented in future features
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [mustCompleteInOrder, setMustCompleteInOrder] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [priorityFirst, setPriorityFirst] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [canStartAny, setCanStartAny] = useState(true);
  
  // Track path priorities
  const [priorityPaths, setPriorityPaths] = useState<string[]>([]);
  
  // Add recipient type selection state
  const [recipientType, setRecipientType] = useState<'classes' | 'groups' | 'students'>('classes');
  
  // Show student detail modal
  const [selectedStudentDetail, setSelectedStudentDetail] = useState<Student | null>(null);
  
  // Show selected recipients detail
  const [showRecipientDetail, setShowRecipientDetail] = useState(false);
  
  // Class and student selection
  const [classes, setClasses] = useState<Class[]>([]);
  const [selectedClasses, setSelectedClasses] = useState<string[]>([]);
  const [selectedClassesData, setSelectedClassesData] = useState<Class[]>([]);
  
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  const [selectedStudentsData, setSelectedStudentsData] = useState<Student[]>([]);
  
  // Update selected classes data when selection changes
  useEffect(() => {
    const selectedClassesData = classes.filter(c => selectedClasses.includes(c.id));
    setSelectedClassesData(selectedClassesData);
  }, [selectedClasses, classes]);
  
  // Update selected students data when selection changes
  useEffect(() => {
    const selectedStudentsData = students.filter(s => selectedStudents.includes(s.id));
    setSelectedStudentsData(selectedStudentsData);
  }, [selectedStudents, students]);
  
  // Fetch classes and students on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const classesData = await dataService.getClasses();
        const studentsData = await dataService.getStudents();
        setClasses(classesData);
        setStudents(studentsData);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    
    fetchData();
  }, []);
  
  // Handle class selection
  const handleClassSelection = (classId: string) => {
    setSelectedClasses(prev => 
      prev.includes(classId)
        ? prev.filter(id => id !== classId)
        : [...prev, classId]
    );
  };
  
  // Handle group selection
  const handleGroupSelection = (groupId: string) => {
    setSelectedGroupIds(prev => 
      prev.includes(groupId)
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };
  
  // Handle select all groups
  const handleSelectAllGroups = () => {
    const activeGroups = studentGroups.filter(group => group.status === 'active');
    const activeGroupIds = activeGroups.map(group => group.id);
    
    if (selectedGroupIds.length === activeGroups.length) {
      setSelectedGroupIds([]);
    } else {
      setSelectedGroupIds(activeGroupIds);
    }
  };
  
  // Handle student selection
  const handleStudentSelection = (studentId: string) => {
    setSelectedStudents(prev => 
      prev.includes(studentId)
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };
  
  // Handle select all students
  const handleSelectAllStudents = () => {
    const filteredStudents = students
      .filter(student => 
        student.firstName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        student.lastName.toLowerCase().includes(searchQuery.toLowerCase())
      )
      .map(student => student.id);
    
    if (selectedStudents.length === filteredStudents.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(filteredStudents);
    }
  };
  
  // Handle select all classes
  const handleSelectAllClasses = () => {
    const allClassIds = classes.map(c => c.id);
    
    if (selectedClasses.length === allClassIds.length) {
      setSelectedClasses([]);
    } else {
      setSelectedClasses(allClassIds);
    }
  };
  
  // Handle path selection - now ensures no duplicates
  const handlePathSelection = (path: LearningPath) => {
    setSelectedPaths(prev => {
      // Check if path is already selected
      const isSelected = prev.some(p => p.id === path.id);
      
      if (isSelected) {
        // If already selected, remove it
        return prev.filter(p => p.id !== path.id);
      } else {
        // If not selected, add it
        return [...prev, path];
      }
    });
  };
  
  // State for drag and drop operations
  const [draggedPath, setDraggedPath] = useState<LearningPath | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  
  // Handle starting drag
  const handleDragStart = (path: LearningPath) => {
    setDraggedPath(path);
  };
  
  // Handle dropping to reorder or add new path
  const handleReorder = (dropIndex: number) => {
    if (!draggedPath) {
      setDragOverIndex(null);
      return;
    }
  
    // Check if the dragged path is already in the selected paths
    const isAlreadySelected = selectedPaths.some(p => p.id === draggedPath.id);
    
    if (isAlreadySelected) {
      // Handle reordering existing path
      const updatedPaths = [...selectedPaths];
      const dragIndex = updatedPaths.findIndex(p => p.id === draggedPath.id);
      
      // Don't do anything if dropping in the same position
      if (dragIndex === dropIndex) {
        setDraggedPath(null);
        setDragOverIndex(null);
        return;
      }
      
      // Remove the dragged item
      const [removed] = updatedPaths.splice(dragIndex, 1);
      
      // Insert at the new position
      updatedPaths.splice(dropIndex, 0, removed);
      
      // Ensure priority paths stay at the top
      const priorityItems = updatedPaths.filter(path => priorityPaths.includes(path.id));
      const nonPriorityItems = updatedPaths.filter(path => !priorityPaths.includes(path.id));
      
      // If a non-priority item is being moved to the priority section, keep priorities at top
      const isPriorityBeingMoved = priorityPaths.includes(draggedPath.id);
      const isMovingToPrioritySection = dropIndex < priorityItems.length && !isPriorityBeingMoved;
      
      if (isMovingToPrioritySection) {
        setSelectedPaths([...priorityItems, ...nonPriorityItems]);
      } else {
        setSelectedPaths(updatedPaths);
      }
    } else {
      // Handle adding a new path from search results
      handlePathSelection(draggedPath);
    }
    
    setDraggedPath(null);
    setDragOverIndex(null);
  };
  
  // Toggle priority status for a path and move to top if priority
  const togglePathPriority = (pathId: string) => {
    setPriorityPaths(prev => {
      const newPriorityPaths = prev.includes(pathId)
        ? prev.filter(id => id !== pathId)
        : [...prev, pathId];
      
      // Reorder paths if setting to priority
      if (!prev.includes(pathId) && newPriorityPaths.includes(pathId)) {
        // Move the path to the top of the list
        setSelectedPaths(currentPaths => {
          const pathToMove = currentPaths.find(p => p.id === pathId);
          if (!pathToMove) return currentPaths;
          
          const otherPaths = currentPaths.filter(p => p.id !== pathId);
          // Put at the beginning of the array
          return [pathToMove, ...otherPaths];
        });
      }
      
      return newPriorityPaths;
    });
  };
  
  // Custom Toggle Switch component
  const ToggleSwitch: React.FC<{
    isChecked: boolean;
    onChange: () => void;
    label: string;
  }> = ({ isChecked, onChange, label }) => {
    return (
      <label className="inline-flex items-center cursor-pointer">
        <div className="relative">
          <input 
            type="checkbox" 
            className="sr-only"
            checked={isChecked}
            onChange={onChange}
          />
          <div className={`block w-10 h-5 rounded-full transition ${isChecked ? 'bg-blue-600' : 'bg-gray-300'}`}></div>
          <div 
            className={`absolute left-0.5 top-0.5 bg-white w-4 h-4 rounded-full transition-transform duration-200 ease-in-out ${
              isChecked ? 'transform translate-x-5' : ''
            }`}
          ></div>
        </div>
        <span className="ml-3 text-sm font-medium text-gray-700">{label}</span>
      </label>
    );
  };
  
  // Filter paths based on multiple criteria
  const getFilteredPaths = () => {
    return availablePaths.filter(path => {
      // Filter by search query
      if (pathSearchQuery) {
        const query = pathSearchQuery.toLowerCase();
        const matchesName = path.name.toLowerCase().includes(query);
        const matchesDescription = path.description.toLowerCase().includes(query);
        if (!matchesName && !matchesDescription) return false;
      }
      
      // Filter by grade (skip filtering if gradeFilter is empty - 'All' option selected)
      if (gradeFilter && path.grade !== gradeFilter) return false;
      
      // Filter by difficulty
      if (difficultyFilter && path.difficulty !== difficultyFilter) return false;
      
      return true;
    });
  };
  
  // Filter students based on search query
  const filteredStudents = students.filter(student => 
    student.firstName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    student.lastName.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  // Handle assignment submission
  const handleAssign = () => {
    // Validate that start date is selected
    if (!startDate) {
      alert('Please select a start date for the assignment.');
      return;
    }
  
    const assignmentData = {
      paths: selectedPaths.map(path => ({
        id: path.id,
        isPriority: priorityPaths.includes(path.id)
      })),
      classes: selectedClasses,
      students: selectedStudents,
      groups: selectedGroupIds,
      startDate,
      endDate,
      constraints: {
        mustCompleteInOrder,
        priorityFirst,
        canStartAny
      }
    };
    
    console.log('Assignment data:', assignmentData);
    
    // Show success dialog instead of alert
    setShowSuccessDialog(true);
  };
  
  // Handle confirmation dialog close and navigation
  const handleSuccessConfirm = () => {
    setShowSuccessDialog(false);
    // Navigate back to learning paths management
    navigate('/learning-paths/manage');
  };
  
  // Go back handler
  const handleBack = () => {
    if (selectedMode === 'paths') {
      setSelectedMode('students');
    } else {
      navigate('/learning-paths/manage');
    }
  };
  
  // Handle next step (from student selection to path selection)
  const handleNext = () => {
    if (selectedMode === 'students' && (selectedClasses.length > 0 || selectedStudents.length > 0 || selectedGroupIds.length > 0)) {
      setSelectedMode('paths');
    }
  };
  
  // Get a summary of selected recipients
  const getRecipientSummary = () => {
    if (recipientType === 'classes' && selectedClasses.length > 0) {
      return `${selectedClasses.length} ${selectedClasses.length === 1 ? 'class' : 'classes'} selected`;
    } else if (recipientType === 'groups' && selectedGroupIds.length > 0) {
      return `${selectedGroupIds.length} ${selectedGroupIds.length === 1 ? 'group' : 'groups'} selected`;
    } else if (recipientType === 'students' && selectedStudents.length > 0) {
      return `${selectedStudents.length} ${selectedStudents.length === 1 ? 'student' : 'students'} selected`;
    }
    return 'No recipients selected';
  };

  return (
    <PageContainer title="Assign Learning Paths" userRole="Teacher">
      {/* Success Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showSuccessDialog}
        title="Assignment Successful"
        message="Learning paths have been successfully assigned to the selected recipients."
        confirmLabel="OK"
        onConfirm={handleSuccessConfirm}
        variant="success"
        showCancelButton={false}
      />
      {/* Header */}
      <div className="mb-6 flex flex-col md:flex-row justify-between items-start md:items-center">
        <div>
          <div className="flex items-center">
            <h1 className="text-2xl font-bold">Assign Learning Path</h1>
            {selectedMode === 'paths' && (
              <Badge variant="primary" className="ml-3">
                {selectedPaths.length} Paths Selected
              </Badge>
            )}
          </div>
          <p className="text-gray-600 mt-1">
            {selectedMode === 'students' 
              ? pathId 
                ? 'Select recipients for the pre-selected learning path' 
                : 'Select students, groups, or classes to assign learning paths to' 
              : 'Configure and assign selected learning paths'}
          </p>
        </div>
        <div className="flex space-x-3 mt-4 md:mt-0">
          <Button 
            variant="secondary" 
            onClick={handleBack}
          >
            {selectedMode === 'paths' ? 'Back to Recipients' : 'Back to Learning Paths'}
          </Button>
          {selectedMode === 'students' && (
            <Button 
              variant="primary" 
              onClick={handleNext}
              disabled={(recipientType === 'classes' && selectedClasses.length === 0) || 
                         (recipientType === 'groups' && selectedGroupIds.length === 0) ||
                         (recipientType === 'students' && selectedStudents.length === 0)}
            >
              Next: Select Paths
            </Button>
          )}
          {selectedMode === 'paths' && (
            <Button 
              variant="primary" 
              onClick={handleAssign}
              disabled={selectedPaths.length === 0}
            >
              Assign Paths
            </Button>
          )}
        </div>
      </div>
      
      {/* Student Selection Mode */}
      {selectedMode === 'students' && (
        <Card className="p-6">
          {/* Recipient Type Selection */}
          <div className="mb-6">
            <h3 className="font-medium text-gray-900 mb-3">Select Recipients</h3>
            <div className="flex space-x-6">
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  className="form-radio h-4 w-4 text-blue-600"
                  checked={recipientType === 'classes'}
                  onChange={() => setRecipientType('classes')}
                />
                <span className="ml-2">Classes</span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  className="form-radio h-4 w-4 text-blue-600"
                  checked={recipientType === 'groups'}
                  onChange={() => setRecipientType('groups')}
                />
                <span className="ml-2">Groups</span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  className="form-radio h-4 w-4 text-blue-600"
                  checked={recipientType === 'students'}
                  onChange={() => setRecipientType('students')}
                />
                <span className="ml-2">Students</span>
              </label>
            </div>
          </div>
          
          {/* Classes Selection - only shown when recipientType is 'classes' */}
          {recipientType === 'classes' && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Classes ({classes.length})</span>
                <button 
                  className="text-xs text-blue-600 hover:text-blue-800"
                  onClick={handleSelectAllClasses}
                >
                  {selectedClasses.length === classes.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-[300px] overflow-y-auto pr-2">
                {classes.map(c => (
                  <div 
                    key={c.id}
                    className={`border rounded-md p-2 cursor-pointer transition-colors ${selectedClasses.includes(c.id) ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}
                    onClick={() => handleClassSelection(c.id)}
                  >
                    <div className="flex items-center">
                      <input 
                        type="checkbox" 
                        checked={selectedClasses.includes(c.id)}
                        onChange={() => handleClassSelection(c.id)}
                        className="mr-2"
                      />
                      <div>
                        <div className="text-sm font-medium">{c.name}</div>
                        <div className="text-xs text-gray-500">Grade {c.grade} • {c.students.length} students</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Groups Tab - only shown when recipientType is 'groups' */}
          {recipientType === 'groups' && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-medium text-gray-900">Select Student Groups</h3>
                <Button 
                  variant="secondary" 
                  size="sm"
                  onClick={handleSelectAllGroups}
                >
                  {selectedGroupIds.length === studentGroups.filter(group => group.status === 'active').length ? 'Deselect All' : 'Select All'}
                </Button>
              </div>
              
              {studentGroups.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No student groups found
                </div>
              ) : (
                <div className="space-y-3">
                  {studentGroups.filter(group => group.status === 'active').map(group => (
                    <div 
                      key={group.id} 
                      className={`flex items-center p-3 rounded-lg border ${
                        selectedGroupIds.includes(group.id)
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:bg-gray-50'
                      } cursor-pointer`}
                      onClick={() => handleGroupSelection(group.id)}
                    >
                      <input 
                        type="checkbox" 
                        className="h-4 w-4 text-blue-600 rounded cursor-pointer" 
                        checked={selectedGroupIds.includes(group.id)}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleGroupSelection(group.id);
                        }}
                      />
                      <div className="ml-3 flex-1">
                        <span className="font-medium">{group.name}</span>
                        <p className="text-sm text-gray-500">{group.students.length} students</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Students Tab - only shown when recipientType is 'students' */}
          {recipientType === 'students' && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Students ({filteredStudents.length})</span>
                <button 
                  className="text-xs text-blue-600 hover:text-blue-800"
                  onClick={handleSelectAllStudents}
                >
                  {selectedStudents.length === filteredStudents.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              
              <div className="mb-4">
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by name..."
                    className="p-2 pl-8 w-full border border-gray-300 rounded-md"
                  />
                  <svg 
                    className="w-5 h-5 absolute left-2 top-2.5 text-gray-400" 
                    fill="currentColor" 
                    viewBox="0 0 20 20" 
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path 
                      fillRule="evenodd" 
                      d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" 
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>
              
              <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                {filteredStudents.map(student => (
                  <div 
                    key={student.id}
                    className={`flex items-center p-3 rounded-lg border ${
                      selectedStudents.includes(student.id)
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:bg-gray-50'
                    } cursor-pointer`}
                    onClick={() => handleStudentSelection(student.id)}
                  >
                    <input 
                      type="checkbox" 
                      className="h-4 w-4 text-blue-600 rounded cursor-pointer" 
                      checked={selectedStudents.includes(student.id)}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleStudentSelection(student.id);
                      }}
                    />
                    <Avatar 
                      src={student.avatar} 
                      alt={`${student.firstName} ${student.lastName}`} 
                      className="ml-3"
                    />
                    <div className="ml-3 flex-1">
                      <span className="font-medium">{student.firstName} {student.lastName}</span>
                      <p className="text-sm text-gray-500">ID: {student.id} • Grade {student.grade}</p>
                    </div>
                    <Button 
                      variant="secondary" 
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedStudentDetail(student);
                      }}
                    >
                      View Detail
                    </Button>
                  </div>
                ))}
                
                {filteredStudents.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    No students found matching your search.
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Assignment Recipients Summary */}
          <div className="mt-8 border-t border-gray-200 pt-6">
            <h3 className="font-medium text-gray-900 mb-4">Assignment Recipients Summary</h3>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex justify-between items-center">
                <div>
                  <span className="font-medium">Recipients:</span>
                  <div className="mt-1 text-sm">
                    {recipientType === 'classes' && selectedClasses.length > 0 && 
                      `${selectedClasses.length} ${selectedClasses.length === 1 ? 'class' : 'classes'}`
                    }
                    {recipientType === 'groups' && selectedGroupIds.length > 0 && 
                      `${selectedGroupIds.length} ${selectedGroupIds.length === 1 ? 'group' : 'groups'}`
                    }
                    {recipientType === 'students' && selectedStudents.length > 0 && 
                      `${selectedStudents.length} ${selectedStudents.length === 1 ? 'student' : 'students'}`
                    }
                    {((recipientType === 'classes' && selectedClasses.length === 0) || 
                      (recipientType === 'groups' && selectedGroupIds.length === 0) ||
                      (recipientType === 'students' && selectedStudents.length === 0)) && 
                      <span className="text-gray-400 italic">No recipients selected</span>
                    }
                  </div>
                </div>
                
                <div className="flex-shrink-0">
                  <Button
                    variant="primary"
                    onClick={handleNext}
                    disabled={(recipientType === 'classes' && selectedClasses.length === 0) || 
                             (recipientType === 'groups' && selectedGroupIds.length === 0) ||
                             (recipientType === 'students' && selectedStudents.length === 0)}
                  >
                    Next: Select Paths
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Student Detail Modal */}
      {selectedStudentDetail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-xl font-bold">{selectedStudentDetail.firstName} {selectedStudentDetail.lastName}</h2>
              <button 
                className="text-gray-500 hover:text-gray-700"
                onClick={() => setSelectedStudentDetail(null)}
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            
            <div className="flex items-center mb-6">
              <Avatar 
                src={selectedStudentDetail.avatar} 
                alt={`${selectedStudentDetail.firstName} ${selectedStudentDetail.lastName}`}
                className="w-16 h-16"
              />
              <div className="ml-4">
                <p><span className="font-medium">ID:</span> {selectedStudentDetail.id}</p>
                <p><span className="font-medium">Email:</span> {selectedStudentDetail.email}</p>
                <p><span className="font-medium">Grade:</span> {selectedStudentDetail.grade}</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h3 className="font-medium mb-2">School Information</h3>
                <p><span className="text-sm text-gray-500">School:</span> {selectedStudentDetail.school.name}</p>
                <p><span className="text-sm text-gray-500">District:</span> {selectedStudentDetail.school.district}</p>
                <p><span className="text-sm text-gray-500">Address:</span> {selectedStudentDetail.school.address}</p>
              </div>
              
              <div>
                <h3 className="font-medium mb-2">Classes</h3>
                <ul className="space-y-1">
                  {selectedStudentDetail.classes.map(classId => {
                    const classInfo = classes.find((c: Class) => c.id === classId);
                    return (
                      <li key={classId} className="text-sm">
                        {classInfo ? classInfo.name : classId}
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
            
            <div className="mt-6 pt-4 border-t border-gray-200 flex justify-end">
              <Button 
                variant="primary" 
                onClick={() => setSelectedStudentDetail(null)}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Path Selection Mode */}
      {selectedMode === 'paths' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Path Search Panel - Always shown */}
          <div className="lg:col-span-1">
            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-2">Search Learning Paths</h2>
              
              <div className="mb-3">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search learning paths..."
                    value={pathSearchQuery}
                    onChange={(e) => setPathSearchQuery(e.target.value)}
                    className="p-2 pl-8 border border-gray-300 rounded-md w-full"
                  />
                  <svg 
                    className="w-5 h-5 absolute left-2 top-2.5 text-gray-400" 
                    fill="currentColor" 
                    viewBox="0 0 20 20" 
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path 
                      fillRule="evenodd" 
                      d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" 
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>
              
              {/* Filters */}
              <div className="space-y-2 mb-3">
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs font-medium text-gray-700">Grade Level</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {['All', 'K', '1', '2', '3', '4', '5', '6', '7', '8'].map((grade) => (
                      <button
                        key={grade}
                        onClick={() => setGradeFilter(grade === 'All' ? '' : (grade === gradeFilter ? '' : grade))}
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          (grade === 'All' && gradeFilter === '') || grade === gradeFilter
                            ? 'bg-primary text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {grade}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs font-medium text-gray-700">Difficulty</span>
                    {difficultyFilter && (
                      <button 
                        onClick={() => setDifficultyFilter('')}
                        className="text-xs text-gray-500 hover:text-gray-700"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => setDifficultyFilter(difficultyFilter === 'Easy' ? '' : 'Easy')}
                      className={`px-3 py-1 rounded-full text-xs font-medium ${
                        difficultyFilter === 'Easy'
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Easy
                    </button>
                    <button
                      onClick={() => setDifficultyFilter(difficultyFilter === 'Medium' ? '' : 'Medium')}
                      className={`px-3 py-1 rounded-full text-xs font-medium ${
                        difficultyFilter === 'Medium'
                          ? 'bg-yellow-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Medium
                    </button>
                    <button
                      onClick={() => setDifficultyFilter(difficultyFilter === 'Hard' ? '' : 'Hard')}
                      className={`px-3 py-1 rounded-full text-xs font-medium ${
                        difficultyFilter === 'Hard'
                          ? 'bg-red-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Hard
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Learning Paths List */}
              <div className="space-y-2 max-h-[calc(90vh-320px)] overflow-y-auto">
                {getFilteredPaths().map(path => {
                  const isAlreadySelected = selectedPaths.some(selectedPath => selectedPath.id === path.id);
                  return (
                    <div 
                      key={path.id} 
                      className={`border rounded-lg p-2 transition-colors ${
                        isAlreadySelected 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                      draggable={!isAlreadySelected}
                      onDragStart={() => !isAlreadySelected && handleDragStart(path)}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div>
                            <h3 className="font-medium text-sm truncate max-w-[360px]" title={path.name}>
  
  {path.name}
</h3>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant={path.difficulty === 'Easy' ? 'success' : path.difficulty === 'Medium' ? 'warning' : 'error'} 
                                size="sm" 
                              >
                                {path.difficulty}
                              </Badge>
                              <span className="text-xs text-gray-500">Grade {path.grade}</span>
                            </div>
                          </div>
                          
                          <div className="flex flex-wrap gap-1 mt-1 mb-1">
                            {path.standards.slice(0, 3).map(standard => (
                              <Badge key={standard.standardId} variant="secondary" size="sm">
                                {standard.standardId}
                              </Badge>
                            ))}
                            {path.standards.length > 3 && (
                              <span className="text-xs text-gray-500">+{path.standards.length - 3} more</span>
                            )}
                          </div>
                        </div>
                        <div className="flex-shrink-0 ml-2 flex items-center">
                          {!isAlreadySelected ? (
                            <button
                              onClick={() => handlePathSelection(path)}
                              className="bg-primary text-white rounded-full p-1 hover:bg-primary-dark transition-colors"
                              title="Add to assignment"
                              draggable
                              onDragStart={() => handleDragStart(path)}
                            >
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                              </svg>
                            </button>
                          ) : (
                            <span className="inline-flex items-center text-green-600" title="Already added">
  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                
                {getFilteredPaths().length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    No learning paths found matching your search.
                  </div>
                )}
              </div>
              <div className="text-center mt-4 text-sm text-gray-500">
  <p>Click the + button or drag standards to add them to your learning path</p>
</div>
            </Card>
          </div>
          
          {/* Selected Paths Panel */}
          <div className="lg:col-span-2">
            <Card className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">Assignment Configuration</h2>
                <div>
                  <Button 
                    variant="secondary" 
                    size="sm"
                    onClick={() => setShowRecipientDetail(!showRecipientDetail)}
                  >
                    {showRecipientDetail ? 'Hide Recipients' : 
                      recipientType === 'classes' && selectedClasses.length > 0 ?
                        `View ${selectedClasses.length} selected ${selectedClasses.length === 1 ? 'class' : 'classes'}` :
                      recipientType === 'groups' && selectedGroupIds.length > 0 ?
                        `View ${selectedGroupIds.length} selected ${selectedGroupIds.length === 1 ? 'group' : 'groups'}` :
                      recipientType === 'students' && selectedStudents.length > 0 ?
                        `View ${selectedStudents.length} selected ${selectedStudents.length === 1 ? 'student' : 'students'}` :
                      'View Recipients'
                    }
                  </Button>
                </div>
              </div>
              
              {/* Recipients Detail - conditional */}
              {showRecipientDetail && (
                <div className="mb-6 border border-gray-200 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-3">Selected Recipients</h3>
                  
                  {recipientType === 'classes' && selectedClasses.length > 0 && (
                    <div className="space-y-2">
                      {selectedClasses.map(classId => {
                        const classInfo = classes.find((c: Class) => c.id === classId);
                        return (
                          <div key={classId} className="p-2 bg-gray-50 rounded flex justify-between items-center">
                            <div>
                              <span className="font-medium">{classInfo?.name || classId}</span>
                              <div className="text-xs text-gray-500">
                                {classInfo?.students.length || 0} students • Grade {classInfo?.grade || 'N/A'}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  
                  {recipientType === 'groups' && selectedGroupIds.length > 0 && (
                    <div className="space-y-2">
                      {selectedGroupIds.map(groupId => {
                        const group = studentGroups.find(g => g.id === groupId);
                        return (
                          <div key={groupId} className="p-2 bg-gray-50 rounded flex justify-between items-center">
                            <div>
                              <span className="font-medium">{group?.name || groupId}</span>
                              <div className="text-xs text-gray-500">
                                {group?.students.length || 0} students
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  
                  {recipientType === 'students' && selectedStudents.length > 0 && (
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {selectedStudents.map(studentId => {
                        const student = students.find((s: Student) => s.id === studentId);
                        return (
                          <div key={studentId} className="p-2 bg-gray-50 rounded flex justify-between items-center">
                            <div className="flex items-center">
                              <Avatar 
                                src={student?.avatar || ''} 
                                alt={student ? `${student.firstName} ${student.lastName}` : studentId}
                                className="w-8 h-8"
                              />
                              <div className="ml-2">
                                <div className="text-sm font-medium">
                                  {student ? `${student.firstName} ${student.lastName}` : studentId}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {student ? `Grade ${student.grade}` : ''}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              
              <h3 className="font-medium text-gray-900 mb-3">Selected Learning Paths</h3>
              
              {selectedPaths.length === 0 ? (
                /* Main drop zone for new paths - only shown when no paths are selected */
                <div 
                  className={`border-2 border-dashed rounded-lg p-4 transition-all duration-200 ${draggedPath && !selectedPaths.some(p => p.id === draggedPath.id) ? 'border-primary bg-primary-light/10' : 'border-gray-300'}`}
                  onDragOver={(e) => {
                    e.preventDefault();
                    // Only show active state if dragging a path not already in the selection
                    if (draggedPath && !selectedPaths.some(p => p.id === draggedPath.id)) {
                      setDragOverIndex(-1); // Special index for the main drop zone
                    }
                  }}
                  onDragLeave={() => setDragOverIndex(null)}
                  onDrop={(e) => {
                    e.preventDefault();
                    // If we're dropping a new path, add it
                    if (draggedPath && !selectedPaths.some(p => p.id === draggedPath.id)) {
                      handlePathSelection(draggedPath);
                      setDraggedPath(null);
                      setDragOverIndex(null);
                    }
                  }}
                >
                  <div className="text-center text-gray-500 text-sm">
                    Drag learning paths here to add to your selection
                  </div>
                </div>
              ) : (
                <div>
                  <div className="mb-4 text-sm text-gray-600">
                    You can drag and drop to reorder the paths below. Priority paths will automatically move to the top.
                  </div>
                  
                  <div className="space-y-3">
                    {/* Drop zone at the beginning for adding new paths */}
                    <div
                      className={`border-2 border-dashed rounded-lg transition-all duration-200 ${
                        dragOverIndex === -1 && draggedPath && !selectedPaths.some(p => p.id === draggedPath.id)
                          ? 'border-primary bg-primary-light/10 p-2' 
                          : 'border-gray-300 p-0 border-opacity-0 h-2'
                      }`}
                      onDragOver={(e) => {
                        e.preventDefault();
                        // Only show active state if dragging a path not already in the selection
                        if (draggedPath && !selectedPaths.some(p => p.id === draggedPath.id)) {
                          setDragOverIndex(-1);
                        }
                      }}
                      onDragLeave={() => setDragOverIndex(null)}
                      onDrop={(e) => {
                        e.preventDefault();
                        // If we're dropping a new path, add it at the beginning
                        if (draggedPath && !selectedPaths.some(p => p.id === draggedPath.id)) {
                          handlePathSelection(draggedPath);
                          setDraggedPath(null);
                          setDragOverIndex(null);
                        }
                      }}
                    >
                      {dragOverIndex === -1 && draggedPath && !selectedPaths.some(p => p.id === draggedPath.id) && (
                        <div className="text-center text-gray-500 text-xs py-1">
                          Drop here to add at the beginning
                        </div>
                      )}
                    </div>
                    
                    {selectedPaths.map((path, index) => (
                      <React.Fragment key={path.id}>
                        {/* Path item with integrated drop zone */}
                        <div
                          className={`relative ${dragOverIndex === index ? 'mt-6' : 'mt-1'} p-3 border-2 rounded-lg bg-white hover:shadow-md transition-shadow ${
                            draggedPath?.id === path.id ? 'border-primary' : 'border-gray-200'
                          }`}
                          draggable
                          onDragStart={() => handleDragStart(path)}
                          onDragOver={(e) => {
                            e.preventDefault();
                            setDragOverIndex(index);
                          }}
                          onDragLeave={(e) => {
                            // Only clear if we're not still over the element
                            const rect = e.currentTarget.getBoundingClientRect();
                            const x = e.clientX;
                            const y = e.clientY;
                            if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
                              setDragOverIndex(null);
                            }
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            handleReorder(index);
                          }}
                        >
                          {/* Visual indicator for drop target */}
                          {dragOverIndex === index && (
                            <div className="absolute -top-4 left-0 right-0 h-3 bg-primary rounded-full"></div>
                          )}
                          
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center">
                                <div 
                                  className="mr-2 p-1 rounded hover:bg-gray-100 cursor-grab flex-shrink-0"
                                  title="Drag to reorder"
                                >
                                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8h16M4 16h16" />
                                  </svg>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center">
                                    <div className="w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-sm font-semibold mr-2">{index + 1}</div>
                                    <h3 className="font-medium text-sm truncate" title={path.name}>{path.name}</h3>
                                  </div>
                                  <div className="flex flex-col gap-0.5 mt-1">
                                    <div className="flex items-center gap-2">
                                      {priorityPaths.includes(path.id) && (
                                        <Badge variant="warning" size="sm">
                                          Priority
                                        </Badge>
                                      )}
                                      <Badge variant={path.difficulty === 'Easy' ? 'success' : path.difficulty === 'Medium' ? 'warning' : 'error'} size="sm">
                                        {path.difficulty}
                                      </Badge>
                                      <span className="text-xs text-gray-500">Grade {path.grade}</span>
                                    </div>
                                    <div className="flex flex-wrap gap-1">
                                      {path.standards.map(standard => (
                                        <Badge key={standard.standardId} variant="secondary" size="sm">
                                          {standard.standardId}
                                        </Badge>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="flex-shrink-0 flex items-center">
                              <ToggleSwitch
                                isChecked={priorityPaths.includes(path.id)}
                                onChange={() => togglePathPriority(path.id)}
                                label="Priority"
                              />
                              <button
                                className="p-1 ml-2 rounded text-gray-400 hover:text-red-500"
                                onClick={() => handlePathSelection(path)}
                                title="Remove path"
                              >
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        </div>
                        
                        {/* Drop zone at the end */}
                        {index === selectedPaths.length - 1 && (
                          <div
                            className={`mt-3 p-3 border-2 border-dashed rounded-lg transition-all duration-200 ${
                              dragOverIndex === index + 1 ? 'border-primary bg-primary-light/10' : 'border-gray-300'
                            }`}
                            onDragOver={(e) => {
                              e.preventDefault();
                              setDragOverIndex(index + 1);
                            }}
                            onDragLeave={() => setDragOverIndex(null)}
                            onDrop={(e) => {
                              e.preventDefault();
                              handleReorder(index + 1);
                            }}
                          >
                            <div className="text-center text-gray-500 text-sm">
                              Drop here to place at the end
                            </div>
                          </div>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                  
                  {/* Assignment Date Settings */}
                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <h3 className="font-medium text-gray-900 mb-3">Assignment Dates</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
                          Start Date <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="date"
                          id="startDate"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                          className="p-2 border border-gray-300 rounded-md w-full"
                          required
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
                          End Date (Optional)
                        </label>
                        <input
                          type="date"
                          id="endDate"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          className="p-2 border border-gray-300 rounded-md w-full"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Assign Button */}
                  <div className="mt-6 pt-4 border-t border-gray-200 flex justify-between items-center">
                    <div className="text-sm text-gray-500">
                      <span className="text-red-500">*</span> Required field
                    </div>
                    <Button 
                      variant="primary" 
                      onClick={handleAssign}
                      disabled={selectedPaths.length === 0 || !startDate}
                      title={selectedPaths.length === 0 ? 'Please select at least one learning path' : !startDate ? 'Please select a start date' : ''}
                    >
                      Assign Learning Paths
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          </div>
        </div>
      )}
    </PageContainer>
  );
};

export default LearningPathAssignmentScreen;